import { useEffect, useMemo, useState } from "react";
import { useKatalogParametrow } from "../../hooks/useKatalogParametrow.js";
import { api } from "../../lib/api.js";
import { bladPonownejOceny, type BladPonownejOceny } from "../../lib/ocenaPonowna.js";
import { domyslnyWybor, uporzadkujWybor } from "../../lib/parametryOceny.js";
import { WyborParametrow } from "../analiza/WyborParametrow.js";
import { IconOstrzezenie } from "../icons/index.js";
import { Button } from "../ui/Button.js";
import { Dialog } from "../ui/Dialog.js";
import { useToast } from "../ui/Toast.js";

/**
 * Okno "Oceń ponownie" (D-1): wybor parametrow, zdanie o platnych wywolaniach i start oceny.
 *
 * Domyslnie zaznaczony jest poprzedni wybor (`poprzedniWybor`, z ostatniego zadania oceny), przepuszczony przez
 * katalog: parametr, ktorego metodyka juz nie ma, odpada, a gdy nic nie zostanie - zaznaczone sa wszystkie wlaczone.
 * Po zleceniu sesja ma od razu status LENSES, wiec `odswiez` pobiera ja jeszcze raz i odpytywanie samo rusza (i samo
 * staje na DONE): widok odswieza sie bez klikania.
 *
 * Gdy rozmowa nie byla jeszcze oceniana (`pierwszaOcena`), okno nazywa sie "Oceń rozmowę" - to ta sama trasa.
 */
export function OknoPonownejOceny({
  otwarty,
  sesjaId,
  poprzedniWybor,
  pierwszaOcena,
  onZamknij,
  odswiez,
}: {
  otwarty: boolean;
  sesjaId: string;
  /** Slugi parametrow z ostatniego zadania oceny (kolejnosc dowolna). */
  poprzedniWybor: readonly string[];
  pierwszaOcena: boolean;
  onZamknij: () => void;
  /** Pobiera sesje jeszcze raz (useSesja.odswiez): po zleceniu status to LENSES i odpytywanie rusza samo. */
  odswiez: () => Promise<void>;
}): React.JSX.Element {
  const toast = useToast();
  const { katalog, odswiez: ponowKatalog } = useKatalogParametrow(otwarty);
  const [recznyWybor, setRecznyWybor] = useState<string[] | null>(null);
  const [trwa, setTrwa] = useState(false);
  const [blad, setBlad] = useState<BladPonownejOceny | null>(null);

  // Kazde otwarcie zaczyna od poprzedniego wyboru, a nie od tego, co zostalo po poprzedniej probie.
  useEffect(() => {
    if (!otwarty) return;
    setRecznyWybor(null);
    setBlad(null);
    setTrwa(false);
  }, [otwarty]);

  const wybrane = useMemo(() => {
    if (katalog.stan !== "gotowy") return null;
    const lista = katalog.katalog.soczewki;
    if (recznyWybor !== null) return uporzadkujWybor(recznyWybor, lista);
    const poprzedni = uporzadkujWybor(poprzedniWybor, lista);
    return poprzedni.length > 0 ? poprzedni : domyslnyWybor(lista);
  }, [katalog, recznyWybor, poprzedniWybor]);

  const mozeStartowac = katalog.stan === "gotowy" && katalog.katalog.soczewki.length > 0;
  const tytul = pierwszaOcena ? "Oceń rozmowę" : "Oceń ponownie";

  async function ocen(): Promise<void> {
    if (trwa || wybrane === null) return;
    if (wybrane.length === 0) {
      setBlad({ pole: "soczewki", tekst: "Zaznacz co najmniej jeden parametr oceny." });
      return;
    }

    setTrwa(true);
    setBlad(null);
    try {
      await api.ocenPonownie(sesjaId, wybrane);
      // TO-1: potwierdzenie zdarzenia; stan oceny niesie karta ("Liczy się") i odpytywanie sesji.
      toast.sukces("Ocena ruszyła. Wyniki pojawią się na tej stronie.");
      await odswiez();
      onZamknij();
    } catch (err) {
      const b = bladPonownejOceny(err);
      if (b.odswiezParametry) {
        setRecznyWybor(null);
        ponowKatalog();
      }
      if (b.odswiezSesje) await odswiez();
      setBlad(b);
    } finally {
      setTrwa(false);
    }
  }

  return (
    <Dialog
      otwarty={otwarty}
      // Trwajace zlecenie nie jest przerywane przez Esc ani klikniecie w tlo: odpowiedz serwera i tak przyjdzie.
      onZamknij={trwa ? () => undefined : onZamknij}
      tytul={tytul}
      akcje={
        <>
          <Button wariant="secondary" onClick={onZamknij} wylaczony={trwa}>
            Anuluj
          </Button>
          <Button wariant="primary" zajety={trwa} wylaczony={!mozeStartowac} onClick={() => void ocen()}>
            {tytul}
          </Button>
        </>
      }
    >
      {otwarty && (
        <>
          <p className="ocena-okno__lead">
            {pierwszaOcena
              ? "Wybierz parametry, według których model ma ocenić tę rozmowę."
              : "Wybierz parametry, według których model ma ocenić tę rozmowę jeszcze raz. Domyślnie zaznaczony jest poprzedni wybór. Wynik nowej oceny zastąpi poprzedni na ekranie oraz w plikach Word i PDF."}
          </p>
          <WyborParametrow
            katalog={katalog}
            wybrane={wybrane ?? []}
            onZmiana={(lista) => {
              setRecznyWybor(lista);
              if (blad?.pole === "soczewki") setBlad(null);
            }}
            onPonow={ponowKatalog}
            blad={blad?.pole === "soczewki" ? blad.tekst : undefined}
            wylaczony={trwa}
          />
          {blad?.pole === "ogolny" && (
            <p className="field-error" role="alert">
              <IconOstrzezenie size={14} />
              {blad.tekst}
            </p>
          )}
        </>
      )}
    </Dialog>
  );
}
